import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { RootState, useAppDispatch } from '../../store';
import { updateUserInfo } from '../../store/actions/OnboardingActions';
import Button from '../shared/TailwindComponents/Button';
import Input from '../shared/TailwindComponents/Input';
import { FormSection, FormActions } from '../shared/FormComponents';
import { getAuth0Email } from '../../auth0-integration/utils/auth0EmailUtils';
import { PiCheckCircleLight } from 'react-icons/pi';
import OnboardingStepCard from './OnboardingStepCard';

const UserOnboardingStep: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user: auth0User } = useAuth0();
  const { t } = useTranslation();
  const onboarding = useSelector((state: RootState) => state.onboarding);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    role: onboarding.user?.role || 'full_access',
    email: '',
  });



  const realEmail = getAuth0Email(auth0User);

  // Auto-populate email from Auth0 when component mounts or realEmail changes.
  // `formData.email` is read as a "have we seeded it yet?" guard, not as an
  // input - listing it would re-run this on every keystroke. The disable has to
  // sit on the dep array, which is the line the rule reports.
  useEffect(() => {
    if (realEmail && !formData.email) {
      setFormData((prev) => ({
        ...prev,
        email: realEmail,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realEmail]);



  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      return;
    }

    // Use pre-assigned role if available, otherwise use selected role
    const roleToSubmit = onboarding.user?.role || formData.role;

    if (!roleToSubmit) {
      return;
    }

    try {
      await dispatch(
        updateUserInfo({
          first_name: formData.firstName.trim(),
          last_name: formData.lastName.trim(),
          role: roleToSubmit,
          email: formData.email.trim(),
        }) as any
      );
    } catch (error) {
      console.error('Error updating user info:', error);
    }
  };

  return (
    <OnboardingStepCard
      as="form"
      onSubmit={handleSubmit}
      title={t('Complete your profile')}
      description={t('Please enter your personal information to set up your account.')}
      footer={
        <FormActions>
          <Button
            type="submit"
            variant="primary"
            size="small"
            disabled={
              !formData.firstName.trim() ||
              !formData.lastName.trim() ||
              onboarding.isLoading
            }
            isLoading={onboarding.isLoading}
          >
            {t('Continue')}
          </Button>
        </FormActions>
      }
    >
      {realEmail && (
        <Input
          type="email"
          label={t('Email')}
          value={formData.email}
          disabled
          // `description`, not a hand-written <p>: `Input` does not forward
          // `helpText` to its FieldShell (Dropdown and CurrencyPicker do).
          description={t('Email verified and secured')}
          suffix={<PiCheckCircleLight className="w-4 h-4 text-success-500" />}
        />
      )}

      <FormSection gridCols={2}>
        <Input
          type="text"
          label={t('First Name')}
          value={formData.firstName}
          onChange={(e) => handleInputChange('firstName', e.target.value)}
          required
        />
        <Input
          type="text"
          label={t('Last Name')}
          value={formData.lastName}
          onChange={(e) => handleInputChange('lastName', e.target.value)}
          required
        />
      </FormSection>
    </OnboardingStepCard>
  );
};

export default UserOnboardingStep;
